import React, { useCallback, useEffect, useMemo, useState } from 'react';
import withStyles from '@mui/styles/withStyles';
import styles from './UserManagement.styles';
import Content from 'studio/components/Content/Content';
import DataGrid from 'studio/components/DataGrid/DataGrid';
import TextField from 'studio/components/TextField';
import { formatDate, formatQuantity, isEmail, pluralize as plrl } from 'studio/helpers';
import Select from 'studio/components/Select';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import PersonAddIcon from '@mui/icons-material/PersonAddRounded';
import Tabs from 'studio/components/Tabs/Tabs';
import Tab from '@mui/material/Tab';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
import { useMessage } from 'studio/components/Message';
import { useLoader } from 'components/loaders/FadeLoader';
import services from 'services';
import Alert from 'studio/components/Alert';
import Link from 'studio/components/Link';
import { userTypes } from 'studio/constants';
import PeopleIcon from '@mui/icons-material/PeopleRounded';
import SendIcon from '@mui/icons-material/SendRounded';
import { paths as adminPaths } from 'studio/App/Admin/Admin';
import { trackEvent } from 'utils/eventTracking';
import _ from 'lodash';
import PageTitle from 'studio/components/PageTitle';

// the maximum number of emails for a new invitation
const maxNbEmails = 100;
// the free version of data grid limits the number of rows per page to at most 100
const pageSize = 100;

const UserManagement = ({ classes, user }) => {
  useEffect(() => {
    trackEvent('Admin Users Opened');
  }, []);

  const userColumns = _.compact([
    { field: 'lastName', headerName: 'Last Name', flex: 1 },
    { field: 'firstName', headerName: 'First Name', flex: 1 },
    { field: 'email', headerName: 'E-mail', flex: 1 },
    { field: 'type', headerName: 'Type', flex: 1 },
    user.organizationUsesTenants && { field: 'tenantName', headerName: 'Tenant Name', flex: 1 }
  ]);

  const invitationColumns = _.compact([
    { field: 'email', headerName: 'E-mail', width: 300 },
    { field: 'userType', headerName: 'User Type', width: 150 },
    { field: 'creationDate', headerName: 'Creation Date', width: 200 },
    { field: 'expirationDate', headerName: 'Expiration Date', width: 200 },
    user.organizationUsesTenants && { field: 'tenantName', headerName: 'Tenant Name', flex: 1 }
  ]);

  const { successMessage, errorMessage } = useMessage();
  const { loading, withLoader } = useLoader();

  // the tenants
  const [tenants, setTenants] = useState(null);
  // the users
  const [users, setUsers] = useState([]);
  // the invitations
  const [invitations, setInvitations] = useState([]);
  // the subscription
  const [subscription, setSubscription] = useState(null);
  // the SAML configuration, initialized to 'undefined' while it is loading
  const [samlConfiguration, setSamlConfiguration] = useState(undefined);
  // the index of the active tab (0 for users, 1 for invitations)
  const [tabIndex, setTabIndex] = useState(0);
  // the ids of the users that are selected in the user table
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  // the ids of the invitations that are selected in the invitation table
  const [selectedInvitationIds, setSelectedInvitationIds] = useState([]);
  // specifies whether to show the dialog to create new invitations
  const [showInvitationDialog, setShowInvitationDialog] = useState(false);
  // the value of the email field in the dialog
  const [email, setEmail] = useState(null);
  // the emails that have been entered in the dialog
  const [emails, setEmails] = useState([]);
  // the selected user type in the dialog
  const [userType, setUserType] = useState();
  // specifies the status of the Delete dialog
  const [dialogDeleteOpen, setDialogDeleteOpen] = useState(false);

  // the total number of users and invitations
  const existingUserQuantity = useMemo(() => users?.length + invitations?.length, [users, invitations]);
  // the rows of the users table
  const userRows = useMemo(
    () =>
      users?.map(user => {
        const tenant = tenants?.find(tenant => tenant.data.id === user.data.tenantId);
        return {
          id: user.data.id,
          lastName: user.data.lastName,
          firstName: user.data.firstName,
          email: user.data.email,
          type: user.data.type,
          tenantName: tenant?.data.name
        };
      }),
    [users]
  );
  // the rows of the invitations table
  const invitationRows = useMemo(
    () =>
      invitations?.map(invitation => {
        const tenant = tenants?.find(tenant => tenant.data.id === invitation.data.tenantId);
        return {
          id: invitation.data.id,
          email: invitation.data.email,
          userType: invitation.data.userType,
          creationDate: formatDate(invitation.data.creationDate, true),
          expirationDate: formatDate(invitation.data.expirationDate, true),
          tenantName: tenant?.data.name
        };
      }),
    [invitations]
  );
  // id ids of the selected users or invitations
  const selectedResourceIds = useMemo(
    () => (tabIndex === 0 ? selectedUserIds : selectedInvitationIds),
    [tabIndex, selectedUserIds, selectedInvitationIds]
  );
  // the number of selected resources
  const nbSelectedResources = useMemo(() => selectedResourceIds.length, [selectedResourceIds]);
  // contains the emails entered in the dialog and the email of the text field (if valid)
  const allEmails = useMemo(() => {
    // deduplicate in case 'email' is already contained in 'emails'
    return [...new Set([...emails, ...(isEmail(email) ? [email] : [])])];
  }, [emails, email]);
  // the subscription's quantities
  const quantities = useMemo(() => subscription?.data.quantities, [subscription]);
  // specifies whether the remaining quantity for the selected user type is sufficient to cover the entered emails
  const remainingQuantityIsSufficient = useMemo(() => {
    const remainingQuantity = quantities?.remaining[userType?.toLowerCase()];
    return !!userType && remainingQuantity >= allEmails.length;
  }, [userType, quantities, allEmails]);
  // specifies if the "Invite" button in the dialog is enabled
  const inviteButtonIsEnabled = useMemo(
    () =>
      // enable the button if valid emails have been entered
      allEmails.length > 0 &&
      // and the number of max emails is not exceeded
      allEmails.length <= maxNbEmails &&
      // and the email field is empty or valid
      (!email || isEmail(email)) &&
      // and the remaining quantity for the selected user type is sufficient to cover the entered emails
      remainingQuantityIsSufficient &&
      // and the creation request is not pending
      !loading,
    [allEmails, email, remainingQuantityIsSufficient, loading]
  );
  // specifies the type of resources currently being viewed based on which tab is shown
  const resourceType = useMemo(() => (tabIndex === 0 ? 'user' : 'invitation'), [tabIndex]);
  // the subscription's upcoming configuration (if any)
  const upcomingConfiguration = useMemo(() => subscription?.data.upcomingConfiguration, [subscription]);
  // the quantity and remaining quantity based on the upcoming configuration for the selected user type
  const [upcomingQuantity, upcomingRemainingQuantity] = useMemo(
    () => [quantities?.upcoming?.[userType?.toLowerCase()], quantities?.upcoming?.remaining[userType?.toLowerCase()]],
    [quantities, userType]
  );
  // specifies if SAML is being used for authentication
  const isSamlAuthentication = useMemo(() => samlConfiguration?.data.isEnabled, [samlConfiguration]);

  // opens the invitation dialog, resetting its state
  const openInvitationDialog = useCallback(() => {
    setEmail(null);
    setEmails([]);
    setUserType(null);
    // use the first user type with non-zero remaining quantity
    const userType =
      (quantities.remaining.viewer > 0 && userTypes.viewer) ||
      (quantities.remaining.creator > 0 && userTypes.creator) ||
      null;
    setUserType(userType);
    setShowInvitationDialog(true);
  }, [quantities]);

  const getTenants = async () => {
    const response = await withLoader(services.getTenants());
    if (response.success) {
      const tenants = response.data.data;
      setTenants(tenants);
    } else {
      const message = response?.response?.data?.data?.message || 'There was an error getting the tenants';
      errorMessage(message);
    }
  };

  const getUsers = useCallback(async () => {
    const result = await withLoader(services.getUsers());
    if (result.success) {
      // we only show non-admin users in the user management view (as we don't want to mislead them with the number of users),
      // but we still show all users e.g. in the role management view (otherwise we wouldn't be able to assign roles to admins)
      const nonAdminUsers = result.data.data.filter(user => !user.data.isAdmin);
      setUsers([...nonAdminUsers]);
    } else {
      const message = result?.response?.data?.data?.message || 'There was an error getting the users';
      errorMessage(message);
    }
  }, [withLoader]);

  const getInvitations = useCallback(async () => {
    const result = await withLoader(services.getInvitations());
    if (result.success) {
      setInvitations([...result.data.data]);
      return;
    }
    const message = result?.response?.data?.data?.message || 'There was an error getting the invitations';
    errorMessage(message);
  }, [withLoader]);

  const getSamlConfiguration = async () => {
    const response = await services.getSamlConfiguration();
    const samlConfiguration = response.data || null;
    setSamlConfiguration(samlConfiguration);
  };

  const getSubscription = useCallback(async () => {
    // get all subscriptions and use the first one
    const response = await withLoader(services.getSubscriptions());
    if (response.success) {
      const subscriptions = response.data;
      setSubscription(subscriptions.data[0]);
    }
  }, [withLoader]);

  const createInvitations = async (emails, userType) => {
    const body = { emails, userType };
    const result = await withLoader(services.createInvitations(body));
    const nbEmails = emails.length;
    if (result.success) {
      setInvitations(prevInvitations => [...prevInvitations, ...result.data.data]);
      const message = `The ${plrl(nbEmails, 'user')} ${plrl(nbEmails, 'has', 'have')} successfully been invited`;
      successMessage(message);
      setShowInvitationDialog(false);
      return;
    }
    const message = `There was an error inviting the ${plrl(nbEmails, 'user')}: ${result.response.data.data.message}`;
    errorMessage(message);
  };

  /**
   * Deletes users or invitations.
   */
  const deleteResources = useCallback(
    async (type, ids) => {
      // choose right variables / functions depending on the specified type
      const [deleteResource, setResources, resources, selectedResourceIds, setSelectedResourceIds] =
        type === 'user'
          ? [services.deleteUser, setUsers, users, selectedUserIds, setSelectedUserIds]
          : [services.deleteInvitation, setInvitations, invitations, selectedInvitationIds, setSelectedInvitationIds];
      // trigger the deletion request(s) and wait for all of them to complete
      const responses = await withLoader(Promise.all(ids.map(id => deleteResource(id))));
      // collect the ids of the resources that were successfully deleted
      const deletedIds = responses.filter(response => response.success).map(response => response.data.data.id);
      // get the number of succeeded and failed deletions
      const successDeleted = deletedIds.length;
      const failedToDelete = ids.length - successDeleted;

      // show success and/or error messages
      if (successDeleted) {
        // show different messages for single / multiple deletions
        const message =
          ids.length === 1
            ? `The ${type} has successfully been deleted`
            : `${successDeleted} ${plrl(successDeleted, type)} ${plrl(
                successDeleted,
                'has',
                'have'
              )} successfully been deleted`;

        successMessage(message);
      }
      if (failedToDelete) {
        const responseData = responses[0].response.data;
        // show different messages for single / multiple deletions
        const message =
          ids.length === 1
            ? `There was an error deleting the ${type}${responseData.data ? `: ${responseData.data.message}` : ''}`
            : `${failedToDelete} ${plrl(failedToDelete, type)} failed to be deleted`;
        errorMessage(message);
      }
      // filter out the resources that were successfully deleted
      setResources(resources.filter(resource => !deletedIds.includes(resource.data.id)));
      // filter out the selected ids of the resources that were successfully deleted
      setSelectedResourceIds(selectedResourceIds.filter(id => !deletedIds.includes(id)));
      setDialogDeleteOpen(false);
    },
    [users, invitations, selectedUserIds, selectedInvitationIds, withLoader]
  );

  const onEmailChange = useCallback(
    (input, add) => {
      // split input into tokens at separation characters: white space characters, ',', ';' (or any combination thereof)
      const tokens = input.replaceAll(/[\s,;:|]+/g, ' ').split(' ');
      const newEmails = tokens.filter(token => isEmail(token));
      // add the new emails to the list of emails if:
      //   - the new emails contain at least one valid email
      //   - 'add' is true or if 'add' is undefined and the last token was a separation character
      if (newEmails.length && (add || (!add && tokens[tokens.length - 1] === ''))) {
        setEmail(null);
        // deduplicate and add emails
        setEmails([...new Set([...emails, ...newEmails])]);
      } else {
        setEmail(input);
      }
    },
    [emails]
  );

  // retrieves the users, invitations and SAML configuration
  useEffect(() => {
    getTenants();
    getUsers();
    getInvitations();
    getSamlConfiguration();
  }, [getUsers, getInvitations]);

  // (re-)retrieves the subscription whenever the number of users or invitations changes
  // (i.e. initially and after creating an invitation or deleting a user/invitation)
  useEffect(() => {
    getSubscription();
  }, [existingUserQuantity]);

  // the "invite" action for the data grids
  const inviteAction = {
    label: 'Invite',
    icon: <PersonAddIcon />,
    tooltip: isSamlAuthentication
      ? 'Inviting users through Veezoo is disabled since you are using single sign-on with SAML. Instead, users have to be given access to Veezoo at your Identity Provider. Please reach out to your IT administrator.'
      : 'Invite new users',
    disabled: loading || !(quantities?.remaining.user > 0) || isSamlAuthentication,
    onClick: openInvitationDialog,
    'data-test': 'inviteUsersButton'
  };

  // the "delete" action for the data grids
  const deleteAction = (selectedResources, type) => ({
    label: 'Delete',
    icon: <DeleteIcon />,
    tooltip:
      selectedResources.length > 0
        ? `Delete the selected ${plrl(selectedResources, type)}`
        : `Select one or more ${plrl(2, type)} to delete`,
    color: 'secondary',
    disabled: selectedResources.length === 0,
    onClick: () => setDialogDeleteOpen(true)
  });

  return (
    <Content
      loading={!tenants || !users || !invitations || !subscription || samlConfiguration === undefined}
      data-test="usersContent"
    >
      <PageTitle icon={PeopleIcon}>Users</PageTitle>

      {!!quantities && (
        <Alert severity={quantities.remaining.user > 0 ? 'info' : 'warning'}>
          <p>
            Your subscription includes
            {isFinite(quantities.user) ? (
              <>
                {' '}
                <b>{formatQuantity(quantities.user, 'user')}</b>, of which there{' '}
                {plrl(quantities.remaining.user, 'is', 'are')} <b>{formatQuantity(quantities.remaining.user)}</b>{' '}
                remaining:
              </>
            ) : (
              <>
                {' '}
                an <b>unlimited</b> number of users
                {isFinite(quantities.remaining.viewer) || isFinite(quantities.remaining.creator) ? ':' : '.'}
              </>
            )}
          </p>
          {(isFinite(quantities.remaining.viewer) || isFinite(quantities.remaining.creator)) && (
            <ul>
              <li>
                <b>{formatQuantity(quantities.remaining.viewer, userTypes.viewer)}</b>
              </li>
              <li>
                <b>{formatQuantity(quantities.remaining.creator, userTypes.creator)}</b>
              </li>
            </ul>
          )}
          {!subscription.data.isEnterprise && (
            <p>
              Go to <Link href={adminPaths.billing}>Billing</Link> and update your subscription to
              {quantities.remaining.user > 0 ? <> change </> : <> increase </>}
              number of included Viewers and/or Creators.
            </p>
          )}
        </Alert>
      )}

      <Tabs value={tabIndex} onChange={(_, index) => setTabIndex(index)}>
        <Tab
          label={
            <div>
              <PeopleIcon />
              Users ({users.length})
            </div>
          }
        />
        <Tab
          label={
            <div>
              <SendIcon />
              Invitations ({invitations.length})
            </div>
          }
        />
      </Tabs>

      <div hidden={tabIndex !== 0}>
        <DataGrid
          className="usersDatagrid"
          pageSize={pageSize}
          rows={userRows}
          columns={userColumns.map(column => ({ ...column, disableClickEventBubbling: true }))}
          checkboxSelection
          onSelectionModelChange={newSelection => {
            const selection = newSelection.selectionModel || newSelection;
            setSelectedUserIds(selection);
          }}
          sortModel={[
            user.organizationUsesTenants ? { field: 'tenantName', sort: 'asc' } : { field: 'lastName', sort: 'asc' }
          ]}
          actions={[inviteAction, deleteAction(selectedUserIds, 'user')]}
          noRowsText="There are no users"
        />
      </div>

      <div hidden={tabIndex !== 1}>
        <DataGrid
          pageSize={pageSize}
          rows={invitationRows}
          columns={invitationColumns.map(column => ({ ...column, disableClickEventBubbling: true }))}
          checkboxSelection
          onSelectionModelChange={newSelection => {
            const selection = newSelection.selectionModel || newSelection;
            setSelectedInvitationIds(selection);
          }}
          sortModel={[
            user.organizationUsesTenants ? { field: 'tenantName', sort: 'asc' } : { field: 'creationDate', sort: 'asc' }
          ]}
          actions={[inviteAction, deleteAction(selectedInvitationIds, 'invitation')]}
          noRowsText="There are no invitations"
        />
      </div>

      <Dialog fullWidth maxWidth="sm" open={showInvitationDialog} onClose={_ => setShowInvitationDialog(false)}>
        <DialogTitle>Invite users</DialogTitle>
        <DialogContent>
          Enter the e-mail addresses of users you would like to invite to Veezoo.
          <TextField
            className={classes.email}
            value={email}
            label="E-mail address"
            onChange={event => onEmailChange(event.target.value, false)}
            onKeyPress={event => {
              if (event.key === 'Enter') onEmailChange(email, true);
            }}
            onPaste={event => {
              event.preventDefault();
              const input = event.clipboardData.getData('Text');
              onEmailChange(input, true);
            }}
            autoFocus
            error={(!!quantities && !remainingQuantityIsSufficient) || allEmails.length > maxNbEmails}
            description={
              (!!quantities && !remainingQuantityIsSufficient && `Not enough ${userType}s left`) ||
              (allEmails.length > maxNbEmails && `You can invite at most ${maxNbEmails} users at a time`) || (
                <>
                  Add or paste multiple addresses using ENTER or separated by space, newline or <code>,;:|</code>
                </>
              )
            }
            data-test="emailTextfield"
          />
          <div className={classes.emails}>
            {emails.map(email => (
              <Chip
                key={email}
                size="small"
                label={email}
                onDelete={() => setEmails(emails.filter(e => e !== email))}
              />
            ))}
            {!!email && <Chip key={email} size="small" label={email} />}
          </div>
          <div className={classes.userType}>
            <Select
              value={userType}
              label="User type"
              description="Viewers are allowed to use Veezoo, Creators can additionally use Veezoo Studio to work on knowledge graphs and manage users"
              values={[
                [userTypes.viewer, !(quantities?.remaining.viewer > 0)],
                [userTypes.creator, !(quantities?.remaining.creator > 0)]
              ]}
              labels={[
                userTypes.viewer + (!(quantities?.remaining.viewer > 0) ? ' (no Viewers left)' : ''),
                userTypes.creator + (!(quantities?.remaining.creator > 0) ? ' (no Creators left)' : '')
              ]}
              onChange={event => setUserType(event.target.value)}
            />
          </div>
          {!!upcomingConfiguration && inviteButtonIsEnabled && allEmails.length > upcomingRemainingQuantity && (
            <div className={classes.updateWarning}>
              <Alert severity="warning">
                You are <b>exceeding</b> the number of {userType}s included in your subscription&apos;s{' '}
                <b>scheduled update</b> ({formatQuantity(upcomingQuantity, userType)}), which will take effect with the
                next billing period on <b>{formatDate(upcomingConfiguration.startDate, true)}</b>.
                <p>
                  The subscription&apos;s scheduled update will be <b>removed</b>.
                </p>
              </Alert>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="primary" disabled={loading} onClick={_ => setShowInvitationDialog(false)}>
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={!inviteButtonIsEnabled}
            onClick={_ => createInvitations(allEmails, userType)}
            data-test="inviteButton"
          >
            Invite {inviteButtonIsEnabled ? `(${allEmails.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog maxWidth="xs" fullWidth open={dialogDeleteOpen} onClose={() => setDialogDeleteOpen(false)}>
        <DialogTitle>Delete {plrl(nbSelectedResources, resourceType)}</DialogTitle>
        <DialogContent>
          {nbSelectedResources === 1
            ? `Do you really want to delete the ${resourceType}?`
            : `Do you really want to delete ${nbSelectedResources} ${plrl(nbSelectedResources, resourceType)}?`}
        </DialogContent>
        <DialogActions>
          <Button autoFocus color="primary" onClick={() => setDialogDeleteOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button color="primary" onClick={() => deleteResources(resourceType, selectedResourceIds)} disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Content>
  );
};

export default withStyles(styles)(UserManagement);
